import { APP_VERSION } from "@dashboard/config";
import { ExtensionsUrls } from "@dashboard/extensions/urls";
import { AppFrame } from "@dashboard/extensions/views/ViewManifestExtension/components/AppFrame";
import { useAppQuery } from "@dashboard/graphql";
import { useHasManagedAppsPermission } from "@dashboard/hooks/useHasManagedAppsPermission";
import useNotifier from "@dashboard/hooks/useNotifier";
import useShop from "@dashboard/hooks/useShop";
import { Box, Button } from "@saleor/macaw-ui-next";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { ExtensionLoadingState } from "./ExtensionLoadingState";
import { extensionConfigMessages } from "./messages";

interface AppIframeViewProps {
  appId: string;
  onBack: () => void;
}

interface StableFrameConfig {
  appId: string;
  appUrl: string;
  accessToken: string;
  coreVersion: string;
}

const DEFAULT_IFRAME_HEIGHT = 650;

export const AppIframeView: React.FC<AppIframeViewProps> = ({ appId, onBack }) => {
  const { hasManagedAppsPermission } = useHasManagedAppsPermission();
  const { data } = useAppQuery({
    variables: { id: appId, hasManagedAppsPermission },
  });
  const shop = useShop();
  const notify = useNotifier();
  const intl = useIntl();
  const [iframeHeight, setIframeHeight] = useState(DEFAULT_IFRAME_HEIGHT);
  const [appReady, setAppReady] = useState(false);
  const appUrl = data?.app?.appUrl;
  const accessToken = data?.app?.accessToken;

  const stableFrameConfig = useMemo<StableFrameConfig | null>(() => {
    if (!shop?.version || !data?.app?.id || !appUrl || !accessToken) {
      return null;
    }

    return { appId: data.app.id, appUrl, accessToken, coreVersion: shop.version };
  }, [accessToken, appUrl, data?.app?.id, shop?.version]);

  useEffect(() => {
    setAppReady(false);
  }, [stableFrameConfig]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const msg = event.data;

      if (!msg) return;

      const height =
        msg.height ?? msg.iframeHeight ?? (msg.type === "resize" ? msg.value : undefined);

      if (typeof height === "number" && height > 0) {
        setIframeHeight(height);
      }
    };

    window.addEventListener("message", handleMessage);

    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const handleHandshakeDone = useCallback(() => setAppReady(true), []);

  const handleError = useCallback(
    () =>
      notify({
        status: "error",
        text: intl.formatMessage(extensionConfigMessages.iframeError),
      }),
    [intl, notify],
  );

  if (!stableFrameConfig) {
    return (
      <Box display="flex" flexDirection="column" gap={3}>
        <Box>
          <Button variant="tertiary" type="button" onClick={onBack}>
            {intl.formatMessage(extensionConfigMessages.backToList)}
          </Button>
        </Box>
        <ExtensionLoadingState
          message={intl.formatMessage(extensionConfigMessages.loadingAppConfiguration)}
        />
      </Box>
    );
  }

  const appCompleteUrl = ExtensionsUrls.resolveAppCompleteUrlFromDashboardUrl(
    ExtensionsUrls.resolveViewManifestExtensionUrl(stableFrameConfig.appId),
    stableFrameConfig.appUrl,
    stableFrameConfig.appId,
  );

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Box>
        <Button variant="tertiary" type="button" onClick={onBack}>
          {intl.formatMessage(extensionConfigMessages.backToList)}
        </Button>
      </Box>
      <Box
        __height={`${iframeHeight}px`}
        __marginLeft="-16px"
        __marginRight="-16px"
        __marginBottom="-16px"
        position="relative"
      >
        {!appReady && (
          <Box
            position="absolute"
            __inset="0"
            display="flex"
            alignItems="center"
            justifyContent="center"
            __backgroundColor="#ffffff"
            __zIndex="10"
          >
            <ExtensionLoadingState
              message={intl.formatMessage(extensionConfigMessages.loadingAppConfiguration)}
            />
          </Box>
        )}
        <AppFrame
          src={appCompleteUrl || stableFrameConfig.appUrl}
          appToken={stableFrameConfig.accessToken}
          appId={stableFrameConfig.appId}
          onError={handleError}
          onHandshakeDone={handleHandshakeDone}
          coreVersion={stableFrameConfig.coreVersion}
          dashboardVersion={APP_VERSION}
        />
      </Box>
    </Box>
  );
};
